import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {InventoryQualityTableColumns, InventoryQualityTableRow} from '../types';
import {ClientSideRowModelModule,  EventApiModule, ModuleRegistry, type ColDef, RowSelectionOptions, themeQuartz} from "ag-grid-community";
import {AgGridAngular} from 'ag-grid-angular';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { BrandScoreRendererComponent } from '../../brands-dashboard-screen/dashboard-table/custom-table-components/brand-score-renderer/brand-score-renderer.component';
import {AgTooltipCellComponent} from '../../../shared/components/ag-tooltip-cell/ag-tooltip-cell.component';
import {LinkCellComponent} from '../columns/link-cell/link-cell.component';
import {ImpressionsRangePipe} from '../../../pipes/impressions-range.pipe';
import {ShortNumberPipe} from '../../../pipes/short-number.pipe';
import {addPercent, round} from '../../../utils/numbers';
import {TableHeaderCellComponent} from '../../../shared/components/table-header-cell/table-header-cell.component';
import {SortClickEvent} from '../../../utils/sort-state';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';
import {NgIf} from '@angular/common';
import { Router } from '@angular/router';
import { ATTENTION_METRIC_NAME, RoutePaths } from '../../../shared/types/common';
import { tooltips } from '../../../utils/tooltips';
import { ShortDisplayValuePipe } from '../../../pipes/short-display-value.pipe';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  EventApiModule,
]);

@Component({
  selector: 'app-sites-table',
  imports: [AgGridAngular, LoaderComponent, NgIf],
  providers: [ImpressionsRangePipe, ShortNumberPipe, ShortDisplayValuePipe],
  templateUrl: './sites-table.component.html',
  styleUrl: './sites-table.component.scss'
})
export class SitesTableComponent {
  @Input() tableData: InventoryQualityTableRow[] = [];
  @Input() columnPrefix: 'Brand' | 'Competitor' | 'Ad' = 'Brand';
  @Input() loading = false;
  @Output() sortChange: EventEmitter<SortClickEvent> = new EventEmitter();

  private router = inject(Router);
  private isMyCampaigns : boolean = this.router.url.includes(RoutePaths.CAMPAIGNS);
  private gridApi?: GridApi;

  colDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    wrapHeaderText: true,
    autoHeaderHeight: true,
    sortable: false,
    resizable: false,
    minWidth: 100,
  };
  gridOptions = {
    suppressMovableColumns: true,
  };

  theme = themeQuartz.withParams({
    headerBackgroundColor: 'rgba(217, 217, 217, 0.3)',
    rowHoverColor: 'rgba(233, 244, 245, 0.85)',
    rowHeight: 65,
    fontSize:'14px',
    headerFontSize:'16px',
    fontFamily:'Poppins',
    headerFontFamily: 'Poppins',
    headerFontWeight: 600,
    headerTextColor: '#717172;',
    cellTextColor: '#717172;',
    headerHeight: 64
  });
  rowSelection: RowSelectionOptions = {
    mode: "multiRow",
    headerCheckbox: false,
    checkboxes: false,
  };

  modules = [ClientSideRowModelModule];

  constructor(
    private impressionsRangePipe: ImpressionsRangePipe,
    private shortDisplayValuePipe: ShortDisplayValuePipe
  ){}

  ngOnInit(){
    this.colDefs = this.getColDef()
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.addEventListener('sortClick' as any, (event: SortClickEvent) => {this.sortChange.emit(event);});
  }


  private getColDef(): ColDef[] {  
    let colDefs: ColDef[] = [
      {
        field: InventoryQualityTableColumns.CATEGORY,
        headerClass: this.isMyCampaigns ? '' : 'first-row',
        headerName: this.isMyCampaigns ? "Content\nCategory" : "Audience\nInterest",
        width: this.isMyCampaigns ? 190 : 175,
        cellRenderer: AgTooltipCellComponent,
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: false,
        }
      },
      {
        field: InventoryQualityTableColumns.BRAND_IMPRESSIONS,
        headerName: `${this.columnPrefix}\nImpressions`,
        width: this.isMyCampaigns ? 120 : 140,
        valueFormatter: (params: any) => {
          if (typeof params.data.minImpressions === 'number' && typeof params.data.maxImpressions === 'number') {
            return this.impressionsRangePipe.transform(params.data.minImpressions, params.data.maxImpressions);
          } else {
            return params.value != null ? this.shortDisplayValuePipe.transform(params.value, true, this.isMyCampaigns ? 500 : undefined) : '';
          }
        },
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: true,
          field:InventoryQualityTableColumns.BRAND_IMPRESSIONS,
        }
      },
      {
        field: InventoryQualityTableColumns.VIEWABILITY,
        headerName: `Viewability`,
        width: this.isMyCampaigns ? 125 : 140,
        valueFormatter: (params: any) => {
          return params.value != null ? `${params.value}%` : '';
        },
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: true,
          field:InventoryQualityTableColumns.VIEWABILITY
        }
      },
      {
        field: InventoryQualityTableColumns.ATTENTION,
        headerName: ATTENTION_METRIC_NAME,
        width: this.isMyCampaigns ? 115 : 130,
        cellRenderer: BrandScoreRendererComponent,
        cellRendererSelector: (params: any) => ({component: BrandScoreRendererComponent, params: {narrow: false}}),
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: true,
          field:InventoryQualityTableColumns.ATTENTION
        }
      },
      {
        field: InventoryQualityTableColumns.REFRESH_INTERVAL,
        width: this.isMyCampaigns ? 110 : 135,
        headerName: `Refresh\nInterval`,
        valueFormatter: (params: any) => {
          return params.value != null ? `${Number(params.value).toFixed(0)}s` : '';
        },
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: true,
          field:InventoryQualityTableColumns.REFRESH_INTERVAL
        }
      },
      {
        field: InventoryQualityTableColumns.DENSITY,
        headerName: `Ad\nDensity`,
        width: this.isMyCampaigns ? 65 : 80,
        valueFormatter: (params: any) => {
          return params.value != null ? `${params.value}%` : '';
        },
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: true,
          field:InventoryQualityTableColumns.DENSITY
        }
      },
      {
        field: InventoryQualityTableColumns.CLICK_RISK,
        headerName: `Unintended\nClick Risk`,
        width: this.isMyCampaigns ? 155 : 170,
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: false,
          tooltip: tooltips.shared.sitesTable[InventoryQualityTableColumns.CLICK_RISK]

        }
      },
      {
        field: InventoryQualityTableColumns.CPM,
        headerName: `Average\nCPM`,
        width: this.isMyCampaigns ? 110 : 135,
        valueFormatter: (params: any) => {
          return params.value != null ? `$${round(Number(params.value),2,true)}` : '';
        },
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: true,
          field:InventoryQualityTableColumns.CPM,
        }
      }
    ];

    const ctrColumn = {
      field: InventoryQualityTableColumns.CTR,
      headerName: `CTR`,
      width: 20,
      valueFormatter: (params: any) => {
        return typeof params.value === 'number' ? addPercent(round(params.value, 2, true)) : 'N/A';
      },
      headerComponent: TableHeaderCellComponent,
      headerComponentParams: {
        sortable: true,
        field:InventoryQualityTableColumns.CTR,
      }
    };

    if (this.tableData.some(row => 'ctr' in row)) {
      colDefs = colDefs.map((col) => {
        switch (col.field) {

          case InventoryQualityTableColumns.DENSITY:
            col.headerName = 'Ad\nDensity';
            break;

          case InventoryQualityTableColumns.CPM:
            col.headerName = 'CPM';
            col.width = 100;
            break;

          case InventoryQualityTableColumns.ATTENTION:
            col.cellRendererSelector = (params: any) => ({
              component: BrandScoreRendererComponent,
              params: { narrow: true }
            });

            return col;

          default:
            if (typeof col.width === 'number') {
              col.width -= 18;
            }
            break;
        }
        return col;
      });

      colDefs.splice(3, 0, ctrColumn);
    }

    if (this.isMyCampaigns) {
      const domainColumn = {
        field: InventoryQualityTableColumns.DOMAIN,
        headerClass: 'first-row',
        headerName: `Website`,
        width: this.isMyCampaigns ? 180 : 166,
        cellRenderer: LinkCellComponent,
        cellClass: this.isMyCampaigns ? 'website-column-cell-campaigns' : '',  
        headerComponent: TableHeaderCellComponent,
        headerComponentParams: {
          sortable: false
        }
      };
      colDefs.unshift(domainColumn);
    }

    return colDefs;

  }
}
